import type { RoutingSkuPolicyRecord, RoutingSkuTargetRecord } from '@nadoutong/contracts';
import type { I18nKey } from '../../i18n/index.ts';
import type { NamespaceSkuGroup, SkuOption } from '../tenantRoutingShared.ts';

/**
 * Target-pool coverage of ONE SKU, as the config sheet's SKU tree shows it
 * (#1493). Deliberately a transcription of the two judgements the sheet
 * already made inline rather than a new rule:
 *
 *   - `SkuPoolSection`'s `emptyPool` chip fired on "no target is enabled",
 *     which lumped "nothing configured" together with "everything switched
 *     off". The tree needs those apart, so `missing` is split out of it;
 *     `disabled` is what `emptyPool` meant for a non-empty pool.
 *   - `unknown` is the pre-existing `targets === undefined` case — this SKU's
 *     own fan-out query has not resolved (or failed). It is NOT `missing`: a
 *     failed read must never be drawn as a routing hole.
 *
 * The namespace-level "covered / missing" statistic keeps its own rule
 * (`targets.length === 0 && policy == null`) because a policy row alone counts
 * as coverage there. That rule is unchanged, and lives in `namespaceMissingSkus`
 * below.
 */
export type SkuCoverageState = 'unknown' | 'missing' | 'disabled' | 'ready';

export function skuCoverageState(
  targets: readonly RoutingSkuTargetRecord[] | undefined,
): SkuCoverageState {
  if (targets === undefined) return 'unknown';
  if (targets.length === 0) return 'missing';
  return targets.some((target) => target.enabled) ? 'ready' : 'disabled';
}

/**
 * SKUs of one namespace this config routes NEITHER by an explicit target NOR
 * by a policy row — i.e. the "missing" half of the `coverage.namespace`
 * statistic, and exactly the set the coverage-fill dialog proposes to write.
 *
 * Shared rather than transcribed: the tree draws this list in warn text and
 * the dialog it opens sends the same list to the server. Two hand-copies of
 * one predicate can drift, and the drift is invisible — the operator reads a
 * name in the tree that the fill then declines to touch, or vice versa.
 */
export function namespaceMissingSkus(
  group: NamespaceSkuGroup,
  targetsBySku: ReadonlyMap<string, readonly RoutingSkuTargetRecord[]>,
  policiesBySku: ReadonlyMap<string, RoutingSkuPolicyRecord | null>,
): readonly SkuOption[] {
  return group.skus.filter((sku) =>
    (targetsBySku.get(sku.skuId)?.length ?? 0) === 0
    && policiesBySku.get(sku.skuId) == null,
  );
}

/**
 * Screen-reader text for the lamp. DESIGN.md §9: lamps are `aria-hidden`
 * decoration, so the state must also exist as text.
 */
export function skuCoverageLabelKey(state: SkuCoverageState): I18nKey {
  switch (state) {
    case 'ready':
      return 'tenant_routing.sku_pools.coverage_ready';
    case 'disabled':
      return 'tenant_routing.sku_pools.coverage_disabled';
    case 'missing':
      return 'tenant_routing.sku_pools.coverage_missing';
    case 'unknown':
      return 'tenant_routing.sku_pools.coverage_unknown';
  }
}

/**
 * Lamp fill. Three lit states plus an unlit one — the Deck palette has no
 * categorical hues, so this is the ok / warn / line-grey ramp and nothing else
 * (`missing` is unlit because nothing is configured; `disabled` is warn
 * because something is configured and switched off).
 */
export function skuCoverageLampClass(state: SkuCoverageState): string {
  switch (state) {
    case 'ready':
      return 'bg-ok';
    case 'disabled':
      return 'bg-warn';
    case 'missing':
      return 'bg-line-strong';
    case 'unknown':
      return 'bg-paper-faint';
  }
}
